"use client"

import { useCallback, useEffect } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"

import { BasketData } from "@/lib/intershop/interfaces/basket/basket"
import { getSbLocaleFromHostAndUri } from "@/lib/locale-helper"

type ExternalBasketPayload = {
  SKU?: string
  sku?: string
  article?: string
  product?: string
  quantity?: number
  Quantity?: number
  items?: ExternalBasketPayload[]
  [key: string]: unknown
}

type ExternalBasketResponse = {
  result: "OK" | "NOK"
  message: string
  basketData?: BasketData | null
}

declare global {
  interface Window {
    ubAddToBasketExternal?: (payload: ExternalBasketPayload) => Promise<ExternalBasketResponse>
  }
}

const allowedMessageOrigins = new Set([
  "https://media.udobaer.at",
  "https://media.udobaer.be",
  "https://media.udobaer.ch",
  "https://media.udobaer.de",
  "https://media.udobaer.eu",
  "https://media.udobaer.fr",
  "https://media.udobaer.it",
  "https://media.udobaer.nl",
])

function getCurrentSbLocale() {
  return getSbLocaleFromHostAndUri(window.location.hostname, window.location.pathname)
}

function isExternalBasketPayload(value: unknown): value is ExternalBasketPayload {
  if (!value || typeof value !== "object") {
    return false
  }

  const payload = value as ExternalBasketPayload
  return Boolean(payload.SKU ?? payload.sku ?? payload.article ?? payload.product ?? payload.items)
}

function parseMessagePayload(data: unknown): ExternalBasketPayload | undefined {
  if (isExternalBasketPayload(data)) {
    return data
  }

  if (typeof data !== "string") {
    return undefined
  }

  if (data === "refresh-minicart" || data === "show-spinner" || data === "hide-spinner") {
    return undefined
  }

  try {
    const parsed = JSON.parse(data)
    return isExternalBasketPayload(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

export default function ExternalBasketBridgeClient() {
  const { setBasketData, setIsLoading } = useUbStorefrontState()

  const addExternalBasketItem = useCallback(
    async (payload: ExternalBasketPayload): Promise<ExternalBasketResponse> => {
      const response = await fetch(`/api/storefront/basket?sbLocale=${encodeURIComponent(getCurrentSbLocale())}`, {
        method: "POST",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })

      const result = (await response.json()) as ExternalBasketResponse

      if (result.basketData) {
        setBasketData(result.basketData)
      }

      return result
    },
    [setBasketData]
  )

  const refreshBasket = useCallback(async () => {
    const response = await fetch(`/api/storefront/basket?sbLocale=${encodeURIComponent(getCurrentSbLocale())}`, {
      cache: "no-store",
    })

    if (response.ok) {
      const basket = (await response.json()) as BasketData | null | undefined
      setBasketData(basket ?? undefined)
    }
  }, [setBasketData])

  useEffect(() => {
    window.ubAddToBasketExternal = addExternalBasketItem

    const handleCustomEvent = (event: Event) => {
      const payload = (event as CustomEvent<ExternalBasketPayload>).detail

      if (isExternalBasketPayload(payload)) {
        void addExternalBasketItem(payload)
      }
    }

    const handleMessage = (event: MessageEvent) => {
      const sameOrigin = event.origin === window.location.origin

      if (!sameOrigin && !allowedMessageOrigins.has(event.origin)) {
        return
      }

      if (event.data === "refresh-minicart") {
        void refreshBasket()
        return
      }

      if (event.data === "show-spinner") {
        setIsLoading(true)
        return
      }

      if (event.data === "hide-spinner") {
        setIsLoading(false)
        return
      }

      const payload = parseMessagePayload(event.data)

      if (payload) {
        void addExternalBasketItem(payload)
      }
    }

    window.addEventListener("ub:add-to-basket-external", handleCustomEvent)
    window.addEventListener("message", handleMessage)

    return () => {
      if (window.ubAddToBasketExternal === addExternalBasketItem) {
        delete window.ubAddToBasketExternal
      }

      window.removeEventListener("ub:add-to-basket-external", handleCustomEvent)
      window.removeEventListener("message", handleMessage)
    }
  }, [addExternalBasketItem, refreshBasket, setIsLoading])

  return null
}
